/* =====================================================================================
   Grants.

   Three levels, and keeping them separate is what makes the module useful rather than a
   list of reminders:

     funder       who gives money, and roughly how much
     call         a specific window with a closing date, which is the unforgiving part
     application  our attempt at one call

   Funders pay in arrears against hard windows and a missed window is usually unrecoverable
   money, so the closing date is the field everything else is arranged around.

   Deleting an application is allowed only while it is still being drafted. Once submitted it
   is part of the record: a rejection with feedback is worth more than a clean list, because
   it is the thing that makes the next application better.
   ===================================================================================== */

import { BACKEND } from './db';
import { TENANT } from '@tenant';

export type AppStatus =
  | 'drafting' | 'internal_review' | 'submitted' | 'under_review'
  | 'interview' | 'awarded' | 'rejected' | 'withdrawn' | 'deferred';

export const APP_STATUSES: AppStatus[] = [
  'drafting', 'internal_review', 'submitted', 'under_review',
  'interview', 'awarded', 'rejected', 'withdrawn', 'deferred',
];

/** Statuses that mean the money question is settled, so they sort to the bottom. */
export const CLOSED: AppStatus[] = ['awarded', 'rejected', 'withdrawn', 'deferred'];

export interface Funder {
  id: string; name: string; short_name: string | null; country: string | null;
  funder_type: string | null; url: string | null;
  typical_award_min: number | null; typical_award_max: number | null;
  currency: string | null; match_funding_required_pct: number | null;
}

export interface Call {
  id: string; funder_id: string; funder_name?: string;
  name: string; reference: string | null; url: string | null;
  opens_on: string | null; closes_on: string | null; decision_expected: string | null;
  max_award: number | null; currency: string | null; our_fit: string | null; scope: string | null;
}

export interface Application {
  id: string; call_id: string | null; funder_id: string | null;
  funder_name?: string; call_name?: string; call_closes?: string | null; call_url?: string | null;
  reference: string | null; title: string; lead_applicant: string | null;
  requested_amount: number | null; match_amount: number | null; currency: string | null;
  submitted_on: string | null; decision_on: string | null;
  status: AppStatus; awarded_amount: number | null; feedback: string | null;
  funding_contract_id: string | null;
}

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

export async function fetchFunders(): Promise<Funder[]> {
  if (!live()) return (TENANT.seed.funder ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('grants').from('funders').select('*').order('name');
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function fetchCalls(): Promise<Call[]> {
  if (!live()) return (TENANT.seed.call ?? []) as never;
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('grants').from('calls')
    .select('*, funder:funder_id(name)')
    .order('closes_on', { ascending: true, nullsFirst: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((c: any) => ({ ...c, funder_name: c.funder?.name ?? null }));
}

export async function fetchApplications(): Promise<Application[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase()
    .schema('grants').from('applications')
    .select('*, funder:funder_id(name), call:call_id(name, closes_on, url)')
    .order('created_at', { ascending: false });
  if (error) throw new Error(error.message);
  return (data ?? []).map((a: any) => ({
    ...a,
    funder_name: a.funder?.name ?? null,
    call_name: a.call?.name ?? null,
    call_closes: a.call?.closes_on ?? null,
    call_url: a.call?.url ?? null,
  }));
}

export async function createFunder(f: {
  name: string; shortName?: string; country?: string; funderType?: string; url?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('grants').from('funders').insert({
    org_id: await orgId(), name: f.name, short_name: f.shortName || null,
    country: f.country || null, funder_type: f.funderType || null, url: f.url || null,
  });
  if (error) throw new Error(error.message);
}

export async function createCall(c: {
  funderId: string; name: string; reference?: string; url?: string;
  closesOn?: string; maxAward?: number; currency?: string; ourFit?: string; scope?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('grants').from('calls').insert({
    org_id: await orgId(), funder_id: c.funderId, name: c.name,
    reference: c.reference || null, url: c.url || null,
    closes_on: c.closesOn || null, max_award: c.maxAward ?? null,
    currency: c.currency || 'GBP', our_fit: c.ourFit || null, scope: c.scope || null,
  });
  if (error) throw new Error(error.message);
}

export async function createApplication(a: {
  title: string; callId?: string | null; funderId?: string | null;
  reference?: string; requestedAmount?: number; currency?: string; leadApplicant?: string;
}) {
  const { supabase, currentMember } = await import('./supabase');
  const me = await currentMember();
  const { error } = await supabase().schema('grants').from('applications').insert({
    org_id: await orgId(), title: a.title,
    call_id: a.callId || null, funder_id: a.funderId || null,
    reference: a.reference || null,
    requested_amount: a.requestedAmount ?? null,
    currency: a.currency || 'GBP',
    lead_applicant: a.leadApplicant || null,
    owner_id: me?.id ?? null,
    status: 'drafting',
  });
  if (error) throw new Error(error.message);
}

export async function updateApplication(id: string, patch: Partial<{
  status: AppStatus; requested_amount: number; awarded_amount: number;
  submitted_on: string; decision_on: string; feedback: string; reference: string;
}>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('grants').from('applications')
    .update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Deleting is allowed only while drafting. A submitted application is part of the record
 * even when it fails: the feedback on a rejection is the most useful thing the process
 * produces, and deleting it to keep a tidy list throws that away.
 */
export async function deleteApplication(id: string, status: AppStatus) {
  if (status !== 'drafting') {
    throw new Error(
      'Only a draft can be deleted. Mark a submitted application withdrawn instead, so the ' +
      'record and any feedback survive.',
    );
  }
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('grants').from('applications').delete().eq('id', id);
  if (error) throw new Error(error.message);
}

/** Contracts an awarded grant can be delivered under. */
export async function fetchContractsForLinking(): Promise<Array<{ id: string; reference: string; title: string }>> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('contracts').from('contracts')
    .select('id, reference, title').order('reference');
  return data ?? [];
}

export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  return Math.ceil((new Date(iso + 'T00:00:00').getTime() - Date.now()) / 86_400_000);
}

/* -------------------------------------------------------------------------------------
   Where to look for calls.

   Deliberately a curated list of search pages rather than an automated scraper. Funder
   sites change layout constantly, an unattended scraper fails silently, and a grants module
   that quietly stopped finding calls would be worse than one that never claimed to. These
   open the funder's own search already filtered, which is one click and always current.
   ------------------------------------------------------------------------------------- */
export const FUNDER_SEARCHES: { name: string; url: string; note: string }[] = [
  { name: 'Innovate UK / Biomedical Catalyst',
    url: 'https://apply-for-innovation-funding.service.gov.uk/competition/search',
    note: 'The anchor for a UK diagnostics raise. Windows are hard and infrequent.' },
  { name: 'UKRI funding finder',
    url: 'https://www.ukri.org/opportunity/',
    note: 'MRC and EPSRC calls, including translational and industrial partnerships.' },
  { name: 'NIHR i4i',
    url: 'https://www.nihr.ac.uk/researchers/funding-opportunities/',
    note: 'Invention for Innovation suits diagnostics with an NHS adoption route.' },
  { name: 'EU Horizon Europe',
    url: 'https://ec.europa.eu/info/funding-tenders/opportunities/portal/screen/opportunities/topic-search',
    note: 'Associated-country status affects eligibility; check before investing effort.' },
  { name: 'EIC Accelerator',
    url: 'https://eic.ec.europa.eu/eic-funding-opportunities/eic-accelerator_en',
    note: 'Equity plus grant. Heavy process, large cheque.' },
  { name: 'Kidney Research UK',
    url: 'https://www.kidneyresearchuk.org/research/for-researchers/',
    note: 'Directly on-topic for transplant and CKD work; smaller awards, strong signalling.' },
];
